import { useState, useEffect } from "react";

import { Button, Modal, Table, Typography } from "antd";
import { CloseOutlined, ExclamationCircleOutlined, FormOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";

import { ErrorResponse } from "utils/types";
import notifier from "utils/notifier";
import { formatDate } from "utils/helpers";

import { CaseManagementModel, GetCmWorkListResponse } from "store/eligibility/actions";
import { SaveTask } from "store/eligibility/mcp/actions";
import { CategoryModel } from "store/eligibility/documents/actions";

import CaseManagementWorkListModal from "components/Eligibility/MemberEligibilityDetailTables/CaseManagementWorkListTab/CaseManagementWorkListModal";
import UploadDocumentModal from "components/Eligibility/MemberEligibilityDetails/UploadDocumentModal";


interface CaseManagementWorkListTabProps {
  getCMWorkList(searchModel: CaseManagementModel): any,
  saveTask(task: SaveTask): any,
  deleteTask(id: number, userId: string): any,
  getCategory(model: CategoryModel): any,
  uploadFilestoAuth(options: Object): any,
  eligibilityMemberInfo: any,
  primaryCareProvider: any,
  isFetchingCM: boolean,
  isUploading: boolean,
  activeTab: string
}

const { confirm } = Modal;
const { Title } = Typography;

const CaseManagementWorkListTab = (props: CaseManagementWorkListTabProps) => {
  const [isCmWorkListModalVisible, setIsCmWorkListModalVisible] = useState<boolean>(false);
  const [cmWorklistUpdateModal, setCmWorklistUpdateModal] = useState({});
  const [updateOrNewTask, setUpdateOrNewTask] = useState('');
  const [isUploadDocumentModalVisible, setIsUploadDocumentModalVisible] = useState(false);
  const [cmWorklistDataSource, setCmWorklistDataSource] = useState<GetCmWorkListResponse[]>([]);
  const [dos, setDos] = useState(''); // placeholder to get and set dos from the upload document model

  const cmWorkListColumns = [
    {
      title: "Created Date",
      key: "RmiCreatedDate",
      dataIndex: "RmiCreatedDate",
      render: (_: string, record: any) => formatDate(record.RmiCreatedDate, 'MM/DD/YYYY'),
      sorter: (a: any, b: any) => a.RmiCreatedDate.localeCompare(b.RmiCreatedDate)
    },
    {
      title: "Task Description",
      key: "RmiTaskDescription",
      dataIndex: "RmiTaskDescription",
      sorter: (a: any, b: any) => a.RmiTaskDescription.localeCompare(b.RmiTaskDescription)
    },
    {
      title: "Task Owner",
      key: "RmiTaskOwner",
      dataIndex: "RmiTaskOwner",
      sorter: (a: any, b: any) => a.RmiTaskOwner.localeCompare(b.RmiTaskOwner)
    },
    {
      title: "Acuity",
      key: "RmiTaskAcuityId",
      dataIndex: "RmiTaskAcuityId",
      sorter: (a: any, b: any) => a.RmiTaskAcuityId - b.RmiTaskAcuityId
    },
    {
      title: "Status",
      key: "RmiTaskStatus",
      dataIndex: "RmiTaskStatus",
      sorter: (a: any, b: any) => a.RmiTaskStatus.localeCompare(b.RmiTaskStatus)
    },
    {
      title: "Due Date",
      key: "dueDate",
      dataIndex: "RmiDueDate",
      render: (_: string, record: any) => formatDate(record.RmiDueDate, 'MM/DD/YYYY'),
      sorter: (a: any, b: any) => {
        if (a && a.RmiDueDate && a.RmiDueDate.length && b && b.RmiDueDate && b.RmiDueDate.length) {
          return a.RmiDueDate.localeCompare(b.RmiDueDate)
        }
        return 0;
      }
    },
    {
      title: "Completed Date",
      key: "RmiCompletedDate",
      dataIndex: "RmiCompletedDate",
      render: (_: string, record: any) => formatDate(record.RmiCompletedDate, 'MM/DD/YYYY'),
      sorter: (a: any, b: any) => {
        if (a && a.RmiCompletedDate && a.RmiCompletedDate.length && b && b.RmiCompletedDate && b.RmiCompletedDate.length) {
          return a.RmiCompletedDate.localeCompare(b.RmiCompletedDate)
        }
        return 0;
      }
    },
    {
      title: "Next Review Date",
      key: "RmiNextReviewDate",
      dataIndex: "RmiNextReviewDate",
      render: (_: string, record: any) => formatDate(record.RmiNextReviewDate, 'MM/DD/YYYY'),
      sorter: (a: any, b: any) => {
        if (a && a.RmiNextReviewDate && a.RmiNextReviewDate.length && b && b.RmiNextReviewDate && b.RmiNextReviewDate.length) {
          return a.RmiNextReviewDate.localeCompare(b.RmiNextReviewDate)
        }
        return 0;
      }
    },
    {
      title: (<><Button
        type="primary"
        icon={<PlusOutlined />}
        size={"small"}
        onClick={() => openCmWorkListModal()}
      >New</Button></>),
      key: "buttons",
      dataIndex: "buttons",
      render: (_: string, record: any) =>
        <>
          <Button className="mr-1" type="primary" shape="circle" icon={<FormOutlined />} size={'small'} onClick={() => editModal(record)} />
          <Button className="mr-1" type="primary" shape="circle" icon={<UploadOutlined />} size={'small'} onClick={() => openUploadDocumentModal()}/>
          <Button type="primary" shape="circle" icon={<CloseOutlined />} size={'small'}  onClick={() => deleteTask(record.RmiUid)} danger />
        </>
    },
  ];

  const openCmWorkListModal = () => {
    setIsCmWorkListModalVisible(true)
    setUpdateOrNewTask('NewTask')
  }

  const isCmWorkListModalVisibleHandler = () => {
    setIsCmWorkListModalVisible(false);
    getCMWorkList();
  }

  const editModal = (data: any) => {
    setUpdateOrNewTask('updateTask');
    setCmWorklistUpdateModal(data);
    setIsCmWorkListModalVisible(true);
  }

  const openUploadDocumentModal = () => {
    setIsUploadDocumentModalVisible(true);
  }

  const isUploadDocumentModalVisibleHandler = () => {
    setIsUploadDocumentModalVisible(false)
  }

  const deleteTask = (id: number) => {
    confirm({
      title: 'Extend Auth',
      content: 'Are you sure you want to extend this auth?',
      icon: <ExclamationCircleOutlined />,
      onOk() {
        props.deleteTask(id, sessionStorage.user_id)
        .then(() => {
          notifier.success('Task deleted successfully.');
          getCMWorkList();
        })
        .catch((err: ErrorResponse) => {
          notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        })
      },
      onCancel() {

      },
    });
  }

  const getCMWorkList = () => {
    const cmWorkListParams = {
      "CompanyId": props.eligibilityMemberInfo.RmiCompanyId,
      "DueDate": null,
      "MembMpiNo": props.eligibilityMemberInfo.RmiMembMpiNo,
      "MembId": props.eligibilityMemberInfo.RmiMembId,
      "PageIndex": 0,
      "PageSize": 10000,
      "ReviewDate": null,
      "TaskOwner": null,
      "UserId": sessionStorage.user_id
    }
    props.getCMWorkList(cmWorkListParams)
      .then((data: GetCmWorkListResponse[]) => {
        console.log(); //adding a console helps re-render the antd table
        setCmWorklistDataSource(data);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  useEffect(() => {
    if (props.activeTab === '6') getCMWorkList();
  }, [props.activeTab]);

  return (
    <>
      <div className="row">
        <div className="col">
          <Title className="ml-3" level={5}>Case Management Worklist</Title>
        </div>
      </div>

      <CaseManagementWorkListModal
        eligibilityMemberInfo={props.eligibilityMemberInfo}
        isCmWorkListModalVisible={isCmWorkListModalVisible}
        isCmWorkListModalVisibleHandler={isCmWorkListModalVisibleHandler}
        primaryCareProvider={props.primaryCareProvider}
        saveTask={props.saveTask}
        cmWorklistUpdateModal={cmWorklistUpdateModal}
        updateOrNewTask={updateOrNewTask}
      />
      <Table
        columns={cmWorkListColumns}
        dataSource={cmWorklistDataSource}
        pagination={{ position: ["bottomCenter"] }}
        loading={props.isFetchingCM}
      />
      <UploadDocumentModal
        claimDos={dos}
        setClaimDos={setDos}
        eligibilityMemberInfo={props.eligibilityMemberInfo}
        isUploadDocumentModalVisibleHandler={isUploadDocumentModalVisibleHandler}
        isUploadDocumentModalVisible={isUploadDocumentModalVisible}
        uploadFilestoAuth={props.uploadFilestoAuth}
        getCategory={props.getCategory}
        isUploading={props.isUploading}
        uploadMultipleCategories={true}
        allowMultipleAttachments={true}
      />
    </>
  );
};

export default CaseManagementWorkListTab;
